import { useState } from "react";
import { Play } from "lucide-react";
import { getYouTubeId, getYouTubeThumbnail, getVimeoId } from "@/lib/media-utils";

interface LazyVideoEmbedProps {
  url: string;
  title?: string;
}

const LazyVideoEmbed = ({ url, title = "Video" }: LazyVideoEmbedProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const youtubeId = getYouTubeId(url);
  const vimeoId = getVimeoId(url);
  const thumbnail = youtubeId ? getYouTubeThumbnail(url) : null;

  if (isPlaying) {
    const embedSrc = youtubeId
      ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`
      : vimeoId
      ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1`
      : null;

    if (!embedSrc) return null;

    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden">
        <iframe
          src={embedSrc}
          title={title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsPlaying(true)}
      className="relative aspect-video w-full rounded-lg overflow-hidden group cursor-pointer bg-muted"
      aria-label={`Play ${title}`}
    >
      {thumbnail ? (
        <img src={thumbnail} alt={title} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <span className="text-muted-foreground font-typewriter">Video</span>
        </div>
      )}
      <div className="absolute inset-0 bg-foreground/30 flex items-center justify-center group-hover:bg-foreground/20 transition-colors">
        <div className="w-16 h-16 rounded-full bg-accent/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Play className="w-7 h-7 text-accent-foreground ml-0.5" />
        </div>
      </div>
    </button>
  );
};

export default LazyVideoEmbed;
